import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Printer, Ship, Zap, DollarSign, Award } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = "h-9 px-3 rounded-full border font-mono-d text-[9px] uppercase tracking-[0.2em] flex items-center gap-1.5 disabled:opacity-60 transition-colors";

/** Opens the stored label PDF in a new tab (auth header needed, so fetch → blob). */
export const openLabel = async (sid, headers) => {
  const r = await axios.get(`${API}/admin/orders/${sid}/easyship/label.pdf`, { headers, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const PrintLabelButton = ({ order, headers }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await openLabel(order.session_id, headers); } catch (err) { toast.error(err?.response?.status === 404 ? "Label isn't ready yet." : "Could not open the label."); } finally { setBusy(false); }
  };
  return (
    <button data-testid={`easyship-print-${order.session_id}`} onClick={run} disabled={busy} className={`${btn} border-[#0a0a0b] bg-[#0a0a0b] text-white hover:bg-[#ff2a85] hover:border-[#ff2a85]`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />} Print label
    </button>
  );
};

const Tag = ({ icon: Icon, children }) => <span className="inline-flex items-center gap-1 rounded-full bg-[#0a0a0b] text-white px-2 py-0.5 font-mono-d text-[8px] uppercase tracking-[0.15em]"><Icon size={9} /> {children}</span>;

/** "Ship with Easyship": compare live courier rates → buy the label → print. */
export const EasyshipButton = ({ order, headers, enabled, onDone }) => {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState(null);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const sid = order.session_id;

  useEffect(() => {
    if (!open) return;
    setRates(null); setErr(null); setPick(null);
    axios.post(`${API}/admin/orders/${sid}/easyship/rates`, {}, { headers })
      .then((r) => { setRates(r.data.rates); setPick(r.data.rates.find((x) => x.best_value)?.courier_service_id || r.data.rates[0]?.courier_service_id || null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load rates."));
  }, [open, sid, headers]);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${sid}/easyship/buy`, { courier_service_id: pick, origin_url: window.location.origin }, { headers });
      onDone(r.data);
      setOpen(false);
      toast.success(r.data.has_label ? `Label bought via ${r.data.courier} — customer emailed tracking` : "Shipment created — label will appear when Easyship finishes it");
      if (r.data.has_label) openLabel(sid, headers).catch(() => {});
    } catch (e) {
      const msg = e?.response?.data?.detail || "Easyship could not buy the label.";
      toast.error(msg, { duration: 8000 });
      if (e?.response?.status === 409) { onDone({ shipment_id: "pending" }); setOpen(false); }
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;
  const chosen = rates?.find((r) => r.courier_service_id === pick);
  return (
    <>
      <button data-testid={`easyship-ship-${sid}`} onClick={() => setOpen(true)} className={`${btn} border-[#e3e2dc] hover:border-[#0a0a0b]`}><Ship size={12} /> Ship with Easyship</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`easyship-dialog-${sid}`} className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase">Ship order #{sid.slice(-8).toUpperCase()}</DialogTitle>
            <DialogDescription>To {order.shipping?.name || order.customer_name} · {[order.shipping?.address?.city, order.shipping?.address?.state, order.shipping?.address?.country].filter(Boolean).join(", ")}. Buying a label charges your Easyship balance and emails the customer their tracking.</DialogDescription>
          </DialogHeader>
          {!rates && !err && <p className="flex items-center gap-2 text-sm text-[#5f5e5a] py-6 justify-center"><Loader2 size={14} className="animate-spin" /> Comparing couriers…</p>}
          {err && <p data-testid={`easyship-rates-error-${sid}`} className="text-sm text-[#ff2a85] bg-[#ff2a85]/10 rounded-2xl p-4">{err}</p>}
          {rates && !rates.length && <p className="text-sm text-[#5f5e5a]">No couriers available for this address and parcel. Check the parcel defaults in Delivery → Easyship.</p>}
          {rates?.length > 0 && (
            <ul data-testid={`easyship-rates-${sid}`} className="max-h-[320px] overflow-y-auto space-y-2 pr-1" data-lenis-prevent>
              {rates.map((r) => (
                <li key={r.courier_service_id}>
                  <label className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${pick === r.courier_service_id ? "border-[#0a0a0b] bg-[#faf9f6]" : "border-[#e3e2dc] hover:border-[#0a0a0b]/40"}`}>
                    <input type="radio" name={`rate-${sid}`} data-testid={`easyship-rate-${r.courier_service_id}`} checked={pick === r.courier_service_id} onChange={() => setPick(r.courier_service_id)} className="accent-[#ff2a85]" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{r.name}</span>
                      <span className="block text-[11px] text-[#5f5e5a]">{r.min_days && r.max_days ? `${r.min_days}–${r.max_days} days` : r.max_days ? `up to ${r.max_days} days` : "delivery time varies"}</span>
                      <span className="mt-1 flex gap-1 flex-wrap">{r.best_value && <Tag icon={Award}>Best value</Tag>}{r.cheapest && <Tag icon={DollarSign}>Cheapest</Tag>}{r.fastest && <Tag icon={Zap}>Fastest</Tag>}</span>
                    </span>
                    <span className="font-display font-extrabold text-base tabular-nums">${Number(r.total_charge).toFixed(2)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid={`easyship-cancel-${sid}`} onClick={() => setOpen(false)} className="h-10 px-5 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] text-xs">Not now</button>
            <button data-testid={`easyship-buy-${sid}`} onClick={buy} disabled={busy || !pick} className="h-10 px-5 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white text-xs font-medium disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />} Buy label{chosen ? ` · $${Number(chosen.total_charge).toFixed(2)}` : ""}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
